import moment from 'moment';
import { createSelector } from 'reselect';
import { isEmpty, filter, values, sortBy } from 'lodash';

import { DATE_DISPLAY_FORMAT } from 'services/Dates';
import {
  extractAndFormatOwners,
  hasBrokerageAdvantage,
  isRegistrationPOA
} from 'services/Accounts';
import { NOT_PENDING_STATUS, getPendingStatusText } from 'services/Beneficiary';
import {
  getAccountsById,
  getCurrentOwnedAccounts,
  getBrokerageAccountsFromSweepMap
} from 'state/account/AccountSelectors';
import {
  CORPORATE,
  INDIVIDUAL,
  JOINT_TENANT,
  ORGANIZATION,
  POWER_OF_ATTORNEY,
  TRUST,
  UGMA_UTMA,
  isEducationSavingsAccount,
  isIndividualAccount,
  isJointAccount,
  SEP_IRA,
  isIRA,
  TRADITIONAL_IRA_BENE_DEATH,
  ROTH_IRA_BENE_DEATH,
  } from 'services/AccountTypes';
import { getCustomerBusinessPartyId } from 'state/customer/CustomerSelectors';
import {
  isOwnedAccount
} from 'services/Products'
import {
  calculateTotalDistribution,
  formatTotalDistribution,
  cleanBeneficiaries,
  filterBrokerageAccounts,
  formatAccount,
  processGroups,
  mapAgreementsWithOwners,
  PRIMARY_ID,
  SECONDARY_ID
} from 'services/BeneficiaryV2';
import {
  isBenesJointToTodEnabled,
  enableInheritedBeneDeceased
} from 'services/FeatureFlags';
export const getBeneficiariesByAccountType = (state) =>
  state.beneficiariesByAccountType;
export const getIsEditingBeneficiaries = (state) =>
  state.isEditingBeneficiaries;
  export const getManageBeneficiaries = (state) =>
  state.managedBeneficiaryGroupV2.managedBeneficiaries;
  export const getPreviousManageBeneficiaries = (state) =>
  state.managedBeneficiaryGroupV2.previousManagedBeneficiaries;
export const getBeneficiaryTableData = createSelector(
  getAccountsById,
  getBrokerageAccountsFromSweepMap,
  getBeneficiariesByAccountType,
  getCustomerBusinessPartyId,
  (
    accountsById,
    linkedBrokerageAccountsMap,
    beneficiariesByAccountType,
    loggedInUserBPID
  ) => {
    //TODO: Filter out response from RSP. Check Brokerage Agreements.
    if (isEmpty(beneficiariesByAccountType) || !accountsById) {
      return {};
    }
    const beneficiaries = beneficiariesByAccountType.map(
      (beneByAccountType) => {
        const { beneficiaries, agreements } = beneByAccountType;

        if (beneficiaries) {
          const cleanedBenes = beneficiaries.map((bene) => {
            let transformedBirthdate = null;
            if (bene.birthDate) {
              transformedBirthdate = moment
                .utc(bene.birthDate)
                .format(DATE_DISPLAY_FORMAT);
            }

            let businessEntityName = null;
            if (bene.businessEntityNames) {
              businessEntityName = bene.businessEntityNames.join(' ');
            }
            return {
              ...bene,
              birthDate: transformedBirthdate,
              businessEntityName
            };
          });

          const accounts = agreements
            .filter((a) => !isEmpty(accountsById[a.accountId]))
            .map((a) => accountsById[a.accountId]);
          const brokerageAccounts = values(linkedBrokerageAccountsMap).filter(
            (brk) =>
              accounts &&
              accounts.length > 0 &&
              brk &&
              !brk.isViewOnly && hasSweepRelationship(brk,accounts, loggedInUserBPID) &&
              brk.accountTypeId === accounts[0].accountTypeId
          );
          const filteredAgreements = accounts.concat(brokerageAccounts);

          let agreementsWithOwners = filteredAgreements;

          if (filteredAgreements) {
            agreementsWithOwners = filteredAgreements.map((agreement) => {
              const { accountId } = agreement;
              if (accountId) {
                const fullAgreement = accountsById[accountId];
                if (fullAgreement) {
                  const {
                    owners,
                    accountName,
                    ticker,
                    accountTypeId,
                  } = fullAgreement;
                  return {
                    ...agreement,
                    accountTypeId,
                    owners,
                    ownersFormatted: extractAndFormatOwners(fullAgreement),
                    accountName,
                    ticker
                  };
                }
              }
              return { ...agreement };
            });
          }

          //SEP IRA registration grouping are not editable online
          const isRegistrationGroupSEP = agreementsWithOwners.every(
            (agreement) => agreement.accountTypeId === SEP_IRA
          );

          //If logged in user is the POA they are not able to edit
          const isRegistrationPOA = agreementsWithOwners.every((agreement) => {
            return (
              agreement.agreementParties &&
              agreement.agreementParties.some((party) => {
                return (
                  party &&
                  party.customer &&
                  party.customer.businessPartyId === loggedInUserBPID &&
                  party.role === 'AGNT'
                );
              })
            );
          });

          const beneficiariesWithIds = cleanedBenes.map((beneficiary, i) => {
            return { ...beneficiary, _id: i };
          });

          return {
            ...beneByAccountType,
            agreements: sortBy(agreementsWithOwners, ['accountName']),
            beneficiaries: beneficiariesWithIds,
            isEditable: !isRegistrationGroupSEP && !isRegistrationPOA,
            retirementAccount: agreementsWithOwners.length && agreementsWithOwners[0].retirementAccount,
            isIraAccount: agreementsWithOwners.length && agreementsWithOwners[0].accountTypeId && isIRA(agreementsWithOwners[0].accountTypeId),
          };
        }

        return {};
      }
    );

    const beneficiaryData = filter(
      beneficiaries,
      (beneGroup) => !isEmpty(beneGroup.agreements)
    );
    const retirementAccounts = beneficiaryData
      .filter((acc) => acc.retirementAccount === true)

    const nonRetirementAccounts = beneficiaryData
      .filter((acc) => acc.retirementAccount === false)

    const sortedRetirementAccounts = sortBeneficiaryGroupings(retirementAccounts) || [];

    const sortedNonRetirementAccounts = sortBeneficiaryGroupings(nonRetirementAccounts) || [];

    return { beneficiaryData, retirementAccounts: sortedRetirementAccounts, nonRetirementAccounts: sortedNonRetirementAccounts };
  }
);

export const getBeneficiaryCardsData = createSelector(
    getAccountsById,
    getBrokerageAccountsFromSweepMap,
    getBeneficiariesByAccountType,
    getCustomerBusinessPartyId,
    (
        accountsById,
        linkedBrokerageAccountsMap,
        beneficiariesByAccountType,
        loggedInUserBPID
    ) => {

      if (isEmpty(beneficiariesByAccountType) || !accountsById) {
        return {};
      }

      const beneficiaries = beneficiariesByAccountType.map((beneByAccountType) => {
        const {beneficiaries: beneficiariesInAccountType, agreements} = beneByAccountType;

        if (!beneficiariesInAccountType) {
          return {};
        }

        const cleanedBenes = cleanBeneficiaries(beneficiariesInAccountType);

        const accounts = agreements
            .filter((a) => !isEmpty(accountsById[a.accountId]))
            .map((a) => accountsById[a.accountId]);

        const brokerageAccounts = filterBrokerageAccounts(linkedBrokerageAccountsMap, accounts, loggedInUserBPID);

        const filteredAgreements = accounts.concat(brokerageAccounts);

        const agreementsWithOwners = mapAgreementsWithOwners(filteredAgreements, accountsById);

        const isRegistrationGroupSEP = agreementsWithOwners.every(
            (agreement) => agreement.accountTypeId === SEP_IRA
        );

        const isRegistrationPOAForAll = agreementsWithOwners.every((agreement) => {
          return isRegistrationPOA(agreement, loggedInUserBPID);
        });

        return {
          ...beneByAccountType,
          agreements: sortBy(agreementsWithOwners, ['accountName']),
          beneficiaries: cleanedBenes,
          isEditable: !isRegistrationGroupSEP && !isRegistrationPOAForAll,
          retirementAccount:  agreementsWithOwners && agreementsWithOwners.length && agreementsWithOwners[0].retirementAccount,
          isIraAccount: agreementsWithOwners && agreementsWithOwners.length && agreementsWithOwners[0].accountTypeId && isIRA(agreementsWithOwners[0].accountTypeId),
          isEsaAccount: agreementsWithOwners && agreementsWithOwners.length && agreementsWithOwners[0].accountTypeId && isEducationSavingsAccount(agreementsWithOwners[0].accountTypeId),
          isIndividualAccount: agreementsWithOwners && agreementsWithOwners.length && agreementsWithOwners[0].accountTypeId && isIndividualAccount(agreementsWithOwners[0].accountTypeId),
          isJointAccount: agreementsWithOwners && agreementsWithOwners.length && agreementsWithOwners[0].accountTypeId && isJointAccount(agreementsWithOwners[0].accountTypeId)
        };
      });
      const beneficiaryData = beneficiaries.filter((beneGroup) => !isEmpty(beneGroup.agreements));
      const sortedRetirementAccounts = beneficiaryData.filter((acc) => acc.retirementAccount === true).map(formatAccount);
      const sortedNonRetirementAccounts = beneficiaryData.filter((acc) => acc.retirementAccount === false).map(formatAccount);
      return {
        individualRetirement: processGroups(sortedRetirementAccounts),
        generalInvestment: processGroups(sortedNonRetirementAccounts)
      };
    }
);
export const getAgreementsGroupedByRegistrationKey = createSelector(
  getBeneficiariesByAccountType,
  (beneficiariesByAccountType) =>
    beneficiariesByAccountType.reduce((acc, account) => {
      if (!acc[account.registrationValue]) {
        acc[account.registrationValue] = [];
      }
      acc[account.registrationValue].push(...account.agreements);
      return acc;
    }, {})
);
export const formatManagedBeneficiaries = createSelector(
    getManageBeneficiaries,
    (managedBeneficiaries) => {
      const primary = managedBeneficiaries.filter(bene => bene.designationId === PRIMARY_ID && (!bene.deleted));
      const secondary = managedBeneficiaries.filter(bene => bene.designationId === SECONDARY_ID && (!bene.deleted));
      return {
        primary,
        secondary,
        primaryTotalAllocation: calculateTotalDistribution(primary),
        primaryTotalAllocationFormatted: formatTotalDistribution(calculateTotalDistribution(primary)),
        secondaryTotalAllocationFormatted: formatTotalDistribution(calculateTotalDistribution(secondary)),
        secondaryTotalAllocation: calculateTotalDistribution(secondary)
      };
    }
);
export const sortBeneficiaryGroupings = (groupings) => {
  if(groupings && groupings.length) {
    return sortBy(groupings, [(a) => a.agreementTypeValue.displayName, (a) => a.agreements[0].ownersFormatted]);
  }
  return [];
};

export const beneficiariesSorting = (a, b) => {
  if (a.agreementTypeValue?.accountType > b.agreementTypeValue?.accountType) {
    return 1;
  }
  if (b.agreementTypeValue?.accountType > a.agreementTypeValue?.accountType) {
    return -1;
  }
  return 0;
};

const getAllBeneficiariesByAccountType = createSelector(
  getAccountsById,
  getBeneficiariesByAccountType,
  (accountsById, beneficiariesByAccountType) => {
    const groups = {};

    if (beneficiariesByAccountType) {
      beneficiariesByAccountType.forEach((beneByAccountType) => {
        const { beneficiaries } = beneByAccountType;
        beneficiaries.forEach((bene) => {
          const { accountDescriptor } = bene;
          const { accountId } = accountDescriptor;
          const account = accountsById[accountId];
          if (account) {
            const { accountType } = account;
            const accountTypeGroup = groups[accountType] || [];
            accountTypeGroup.push(bene);
            groups[accountType] = accountTypeGroup;
          }
        });
      });
    }

    return groups;
  }
);

export const getPendingStatusByAccountType = createSelector(
  getAllBeneficiariesByAccountType,
  (allBeneficariesByAccountType) => {
    const groups = {};

    for (const accountType in allBeneficariesByAccountType) {
      const beneficiariesForAccountType =
        allBeneficariesByAccountType[accountType];
      const isPending = beneficiariesForAccountType.some((beneficiary) => {
        return (
          beneficiary.pendingUpdateId !== null &&
          beneficiary.pendingUpdateId !== NOT_PENDING_STATUS
        );
      });
      let pendingText = '';
      if (isPending) {
        const beneficiary = beneficiariesForAccountType.find((beneficiary) => {
          return (
            beneficiary.pendingUpdateId !== null &&
            beneficiary.pendingUpdateId !== NOT_PENDING_STATUS
          );
        });
        const { pendingUpdateId } = beneficiary;
        pendingText = getPendingStatusText(pendingUpdateId);
      }

      groups[accountType] = {
        isPending,
        pendingText
      };
    }

    return groups;
  }
);

export const getHasEligibleEsa = createSelector(
  getBeneficiariesByAccountType,
  getAccountsById,
  (beneficiariesByAccountType, accountsById) => {
    return beneficiariesByAccountType.some((reg) => {
      if (reg.agreements && reg.agreements[0]) {
        const fullAccount = accountsById[reg.agreements[0].accountId];
        if (fullAccount) {
          return isEducationSavingsAccount(fullAccount.accountTypeId);
        }
      }
    });
  }
);

const isNotBeneQualifiedAccount = (accountTypeId) => {
  const notQualifiedAccountTypes = [
    UGMA_UTMA,
    TRUST,
    CORPORATE,
    ORGANIZATION,
    POWER_OF_ATTORNEY
  ];
    if(!enableInheritedBeneDeceased()) {
      notQualifiedAccountTypes.push(TRADITIONAL_IRA_BENE_DEATH);
      notQualifiedAccountTypes.push(ROTH_IRA_BENE_DEATH);
    }
    if(!isBenesJointToTodEnabled()) {
      notQualifiedAccountTypes.push(INDIVIDUAL);
      notQualifiedAccountTypes.push(JOINT_TENANT);
    }
    return notQualifiedAccountTypes.some((notQualifiedAccountType) => {
    return notQualifiedAccountType === accountTypeId;
  });
};

export const hasSweepRelationship = (brk,accounts,loggedInUserBPID) => {
  return ((isOwnedAccount(brk) ||
          brk.agreementParties.some(agrParty => agrParty.customer &&
              agrParty.customer.businessPartyId === loggedInUserBPID && agrParty.role === "AGNT" )) &&
      accounts.some(a => a.accountId === brk.sweepAccountId))
}
//all the accounts should either be a notBeneQualifiedAccount or has the BrokerageAdvantage product.
export const isNotQualifiedForBeneficiaries = createSelector(
  getCurrentOwnedAccounts,
  (accounts) => {
    return (
      accounts.every(
          ({ accountTypeId, serviceIds }) =>
              hasBrokerageAdvantage(serviceIds) ||
          isNotBeneQualifiedAccount(accountTypeId)
      )
    );
  }
);
